import { RefObject } from 'react';

import { getStateFromPath } from '@react-navigation/native';

import { useLinkingStore } from '@/feature/entry-share/hooks/use-linking';
import { useEventsTabsStore } from '@/hooks/use-events-tabs-store';
import { isSocialWalletProduct } from '@/hooks/use-has-social-wallet';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { save } from '@/utils/async-storage';
import { isWeb } from '@/utils/constants-platform-specific';
import { mapVerticalToProduct, saveDeeplinkReferredProduct } from '@/utils/deeplink-referred-product';
import {
    cleanDeepLinkPath,
    isArcadeSpecificUrl,
    isCasinoSpecificUrl,
    isPicksSpecificUrl,
    isPredictionSpecificUrl,
    isRipsSpecificUrl,
    isSweepsSpecificUrl,
    requiresManualWebHandling,
} from '@/utils/linking';
import { logger } from '@/utils/logging';
import { isTrustedWebviewUrl } from '@/utils/webview';

import {
    ProductTransitionTargetScreen,
    useProductTransitionStore,
} from '../feature/product-transition/use-product-transition-store';
import { useUserStore } from '../hooks/use-user-store';
import { getProductLobby, getProductRoot } from '../utils/product';
import { RootStackParamList } from './types';

// Routes that should not be accessible via direct link
const BLACKLISTED_ROUTES = ['picks/submit-entry', 'picks/cancel-entry'];

type NavigationState = ReturnType<typeof getStateFromPath>;
type GetStateFromPathOptions = Parameters<typeof getStateFromPath>[1];

interface ProductConfig {
    home: string;
    lobbyTab: string;
}

interface LinkingContext {
    isProductActive: (product: Product) => boolean;
    isProductAvailable: (product: Product) => boolean;
    isProductHiddenByUser: (product: Product) => boolean;
    availableProducts: Product[];
    guestAllowed: boolean;
    routeNameRef: RefObject<string | undefined>;
}

/**
 * Checks if a route is blacklisted and should redirect to splash
 */
const isBlacklistedRoute = (path: string): boolean => {
    return BLACKLISTED_ROUTES.some(route => path.includes(route));
};

/**
 * Extracts and saves UTM parameters from the URL
 */
const handleUtmParameters = (path: string) => {
    if (!path.includes('utm')) {
        return;
    }

    const utmParameters: Record<string, string> = {};
    const parameters = new URLSearchParams(path);

    parameters.forEach((value, key) => {
        if (key.includes('utm')) {
            utmParameters[key] = value;
        }
    });

    if (Object.keys(utmParameters).length > 0) {
        save('utm_parameters', utmParameters);
    }
};

/**
 * Extracts and saves promo code from OneLink deep link
 */
const handlePromoCode = (path: string) => {
    if (!path.includes('deep_link_sub1')) {
        return;
    }

    logger.info('New onelink link', path);
    const params = new URLSearchParams(path);
    // deep_link_sub1 is used for filling the promo code
    const promoCode = params.get('deep_link_sub1');

    if (promoCode) {
        save('promo_code', promoCode);
    }
};

/**
 * Extracts the product vertical from OneLink deep link (deep_link_sub2)
 * and persists it so the registration flow can apply it.
 */
const handleDeepLinkVertical = (path: string) => {
    if (!path.includes('deep_link_sub2')) {
        return;
    }

    const params = new URLSearchParams(path);
    const vertical = params.get('deep_link_sub2');

    if (vertical) {
        const product = mapVerticalToProduct(vertical);
        if (product) {
            saveDeeplinkReferredProduct(product);
            useJurisdictionStore.getState().actions.setProduct(product);
        }
    }
};

const handleWebSpecificProcessing = (path: string) => {
    handleUtmParameters(path);
    handlePromoCode(path);
    handleDeepLinkVertical(path);
};

const getProductLobbyConfig = (product: Product): ProductConfig | undefined => {
    const productLobbyMap: Partial<Record<Product, ProductConfig>> = {
        [Product.Pickem]: { home: 'PickemHome', lobbyTab: 'Lobby' },
        [Product.Sweeps]: { home: 'SweepsHome', lobbyTab: 'SweepsLobby' },
        [Product.Arcade]: { home: 'ArcadeHome', lobbyTab: 'ArcadeLobby' },
        [Product.Casino]: { home: 'CasinoHome', lobbyTab: 'CasinoLobby' },
        [Product.Prediction]: { home: 'PredictionHome', lobbyTab: 'PredictionLobby' },
        [Product.Rips]: { home: 'RipsHome', lobbyTab: 'RipsLobby' },
    };

    return productLobbyMap[product];
};

const getHiddenProductRedirectState = (isProductHiddenByUser: boolean, product: Product): NavigationState | null => {
    if (!isProductHiddenByUser || useUserStore.getState().actions.sessionHasExpired()) {
        return null;
    }

    useLinkingStore.getState().setUnavailableProductRedirect(product);

    const activeProduct = useJurisdictionStore.getState().product;
    const config = getProductLobbyConfig(activeProduct);

    if (!config) {
        return null;
    }

    return { routes: [{ name: config.home }] };
};

/**
 * Handles the /promos deep link by navigating to the active product's lobby promohub
 * All the product have the promohub nested in the lobby stack
 */
const handlePromoHubRoute = (path: string): NavigationState | null => {
    const cleanPath = cleanDeepLinkPath(path);

    if (cleanPath !== 'promos') {
        return null;
    }

    const activeProduct = useJurisdictionStore.getState().product;
    const jurisdiction = useJurisdictionStore.getState().jurisdiction;

    if (!jurisdiction || activeProduct === Product.None) {
        return { routes: [{ name: 'Splash' }] };
    }

    const config = getProductLobbyConfig(activeProduct);

    if (config) {
        return {
            routes: [
                {
                    name: config.home,
                    state: {
                        routes: [
                            {
                                name: config.lobbyTab,
                                state: {
                                    routes: [{ name: 'LobbyPromohub' }],
                                },
                            },
                        ],
                    },
                },
            ],
        };
    }

    return { routes: [{ name: 'Splash' }] };
};

/**
 * Coin products buy coins in the shop, so /deposit opens CoinPackageScreen instead of DepositScreen
 */
const redirectDepositToCoinShop = (path: string): string => {
    const hasSocialWallet = isSocialWalletProduct(useJurisdictionStore.getState().product);

    return hasSocialWallet && cleanDeepLinkPath(path).replace(/\/+$/, '') === 'deposit' ? 'purchase' : path;
};

/**
 * Prevents users from bypassing the auth flow via deeplinks
 */
const handleAuthProtection = (path: string, context: LinkingContext): NavigationState | null => {
    if (!useUserStore.getState().actions.sessionHasExpired() || context.guestAllowed) {
        return null;
    }

    const isAuthPath = path.includes('auth') || path.includes('login');
    const isLoginRoute = context.routeNameRef.current === 'Login';

    if (isWeb) {
        if (requiresManualWebHandling(path)) {
            // save share entry / partner links to handle once the user logs in
            save('webPendingLink', path);
        }

        if (!isAuthPath) {
            return {
                routes: [
                    {
                        name: 'Splash',
                        params: {
                            redirectPath: path,
                        },
                    },
                ],
            };
        }

        return null;
    }

    useLinkingStore.getState().setPendingLink(path);

    if (isLoginRoute) {
        return undefined;
    }

    return { routes: [{ name: 'Splash' }] };
};

const handlePicksRoute = (
    path: string,
    picksActive: boolean,
    picksAvailable: boolean,
    picksHiddenByUser: boolean,
    opts: GetStateFromPathOptions
): NavigationState | null => {
    if (!isPicksSpecificUrl(path)) {
        return null;
    }

    const jurisdiction = useJurisdictionStore.getState().jurisdiction;
    if (jurisdiction && (picksActive || picksAvailable)) {
        useJurisdictionStore.getState().actions.setProduct(Product.Pickem);

        if (path.startsWith('entry/')) {
            // App might be opened on a different product so store the path and go to picks home
            useLinkingStore.getState().setPendingLink(path);
            return {
                routes: [
                    {
                        name: 'PickemHome',
                    },
                ],
            };
        }

        return getStateFromPath(path, opts);
    }

    return (
        getHiddenProductRedirectState(picksHiddenByUser, Product.Pickem) ?? {
            routes: [{ name: 'Splash' }],
        }
    );
};

const handleArcadeRoute = (
    path: string,
    arcadeActive: boolean,
    arcadeAvailable: boolean,
    arcadeHiddenByUser: boolean,
    opts: GetStateFromPathOptions
): NavigationState | null => {
    if (!isArcadeSpecificUrl(path)) {
        return null;
    }

    const jurisdiction = useJurisdictionStore.getState().jurisdiction;
    if (jurisdiction && (arcadeActive || arcadeAvailable)) {
        useJurisdictionStore.getState().actions.setProduct(Product.Arcade);
        return getStateFromPath(path, opts);
    }
    return (
        getHiddenProductRedirectState(arcadeHiddenByUser, Product.Arcade) ?? {
            routes: [{ name: 'Splash' }],
        }
    );
};

/**
 * Parses a `casino/game/<gameId>` deep link into a navigation state that switches to the Casino
 * product and deep-launches the given game on top of the Casino lobby (mirrors the Sweeps Blackjack
 * tab). Returns null when the path isn't a casino game launch. A missing gameId lands on the lobby.
 */
const getCasinoGameLaunchState = (path: string): NavigationState | null => {
    const cleanPath = cleanDeepLinkPath(path);
    const segments = cleanPath.split('/');

    if (segments[0] !== 'casino' || segments[1] !== 'game') {
        return null;
    }

    let gameId: string | undefined;
    if (segments[2]) {
        try {
            gameId = decodeURIComponent(segments[2]);
        } catch {
            // leave undefined so we fall back to the lobby.
        }
    }

    useProductTransitionStore
        .getState()
        .actions.triggerTransition(
            Product.Casino,
            gameId ? { name: 'CasinoGameWebView', params: { gameId } } : undefined
        );

    return {
        routes: [{ name: getProductRoot(Product.Casino), params: { screen: getProductLobby(Product.Casino).tab } }],
    };
};

/**
 * Parses `casino/free-spins` deep link to Casino Free Spins tab
 */
const getCasinoFreeSpinsLaunchState = (path: string): NavigationState | null => {
    const cleanPath = cleanDeepLinkPath(path);
    const segments = cleanPath.split('/');

    if (segments[0] !== 'casino' || segments[1] !== 'free-spins') {
        return null;
    }

    useProductTransitionStore.getState().actions.triggerTransition(Product.Casino, {
        name: 'CasinoHome',
        params: {
            screen: 'CasinoLobby',
            params: { screen: 'CasinoLobbyScreen', params: { category: 'Free Spins' } },
        },
    });

    return {
        routes: [{ name: getProductRoot(Product.Casino), params: { screen: getProductLobby(Product.Casino).tab } }],
    };
};

const handleCasinoRoute = (
    path: string,
    casinoActive: boolean,
    casinoAvailable: boolean,
    casinoHiddenByUser: boolean,
    opts: GetStateFromPathOptions
): NavigationState | null => {
    if (!isCasinoSpecificUrl(path)) {
        return null;
    }

    const jurisdiction = useJurisdictionStore.getState().jurisdiction;
    if (jurisdiction && (casinoActive || casinoAvailable)) {
        const gameLaunchState = getCasinoGameLaunchState(path);
        if (gameLaunchState) {
            return gameLaunchState;
        }

        const freeSpinsLaunchState = getCasinoFreeSpinsLaunchState(path);
        if (freeSpinsLaunchState) {
            return freeSpinsLaunchState;
        }

        useJurisdictionStore.getState().actions.setProduct(Product.Casino);
        return getStateFromPath(path, opts);
    }

    return (
        getHiddenProductRedirectState(casinoHiddenByUser, Product.Casino) ?? {
            routes: [{ name: 'Splash' }],
        }
    );
};

const handlePredictionRoute = (
    path: string,
    predictionActive: boolean,
    predictionAvailable: boolean,
    predictionHiddenByUser: boolean,
    opts: GetStateFromPathOptions
): NavigationState | null => {
    if (!isPredictionSpecificUrl(path)) {
        return null;
    }

    const jurisdiction = useJurisdictionStore.getState().jurisdiction;
    if (jurisdiction && (predictionActive || predictionAvailable)) {
        useJurisdictionStore.getState().actions.setProduct(Product.Prediction);
        return getStateFromPath(path, opts);
    }

    return (
        getHiddenProductRedirectState(predictionHiddenByUser, Product.Prediction) ?? {
            routes: [{ name: 'Splash' }],
        }
    );
};

const handleSweepsRoute = (
    path: string,
    sweepsActive: boolean,
    sweepsAvailable: boolean,
    sweepsHiddenByUser: boolean,
    opts: GetStateFromPathOptions
): NavigationState | null => {
    if (!isSweepsSpecificUrl(path)) {
        return null;
    }

    const jurisdiction = useJurisdictionStore.getState().jurisdiction;
    if (jurisdiction && (sweepsActive || sweepsAvailable)) {
        useJurisdictionStore.getState().actions.setProduct(Product.Sweeps);
        return getStateFromPath(path, opts);
    }

    return (
        getHiddenProductRedirectState(sweepsHiddenByUser, Product.Sweeps) ?? {
            routes: [{ name: 'Splash' }],
        }
    );
};

const handleRipsRoute = (
    path: string,
    ripsActive: boolean,
    ripsAvailable: boolean,
    ripsHiddenByUser: boolean,
    opts: GetStateFromPathOptions
): NavigationState | null => {
    if (!isRipsSpecificUrl(path)) {
        return null;
    }

    const jurisdiction = useJurisdictionStore.getState().jurisdiction;
    if (jurisdiction && (ripsActive || ripsAvailable)) {
        useJurisdictionStore.getState().actions.setProduct(Product.Rips);
        return getStateFromPath(path, opts);
    }

    return (
        getHiddenProductRedirectState(ripsHiddenByUser, Product.Rips) ?? {
            routes: [{ name: 'Splash' }],
        }
    );
};

/**
 * Handles default route when no product-specific handler matches
 */
const handleDefaultRoute = (
    path: string,
    availableProducts: Product[],
    anyActiveProduct: boolean,
    opts: GetStateFromPathOptions
): NavigationState => {
    if (availableProducts.length > 0) {
        // if the path is neither picks, arcade, casino, prediction, rips, nor social (sweeps), check if at least one product is available
        if (anyActiveProduct) {
            return getStateFromPath(path, opts);
        }

        return { routes: [{ name: 'Splash' }] };
    }

    return { routes: [{ name: 'Splash' }] };
};

const SWITCHABLE_PRODUCTS = Object.values(Product).filter(p => p !== Product.None) as string[];

/**
 * When path is `<prefix>/<product>` and product is a valid switchable Product,
 * trigger the product transition overlay and navigate to the target product root.
 */
const handleProductTransitionRoute = (path: string): NavigationState | null => {
    const cleanPath = cleanDeepLinkPath(path);
    const segments = cleanPath.split('/');
    const lastSegment = segments[segments.length - 1];

    if (segments.length < 2 || !SWITCHABLE_PRODUCTS.includes(lastSegment)) {
        return null;
    }

    const product = lastSegment as Product;
    const activeProduct = useJurisdictionStore.getState().product;
    const pathPrefix = segments.slice(0, -1).join('/');
    const target = PATH_PREFIX_TO_SCREEN[pathPrefix];

    if (target && !target.products.includes(product)) {
        return { routes: [{ name: 'Splash' }] };
    }

    if (product === activeProduct) {
        return null;
    }

    useProductTransitionStore.getState().actions.triggerTransition(product, target?.screen);

    return {
        routes: [{ name: getProductRoot(product), params: { screen: getProductLobby(product).tab } }],
    };
};

type ProductTransitionTarget = {
    screen: ProductTransitionTargetScreen;
    products: Product[];
};

/**
 * Maps path prefixes (from the linking config) to the screen to navigate to after the
 * product transition completes. Only screens that need product-switch support need to be listed here.
 */
export const PATH_PREFIX_TO_SCREEN: Partial<Record<string, ProductTransitionTarget>> = {
    purchase: {
        screen: { name: 'DepositStack', params: { screen: 'CoinPackageScreen' } },
        products: [Product.Sweeps, Product.Casino],
    },
};

// in-app code may open any host in ModalWebView, but a deeplink may only open first-party pages
const isUntrustedWebViewPath = (path: string, opts: GetStateFromPathOptions): boolean => {
    const webViewRoute = getStateFromPath(path, opts)?.routes?.find(route => route.name === 'ModalWebView');
    if (!webViewRoute) {
        return false;
    }

    const { params } = webViewRoute;
    if (params && 'uri' in params && typeof params.uri === 'string' && isTrustedWebviewUrl(params.uri)) {
        return false;
    }

    logger.warn('Rejected untrusted webview deeplink', params);
    return true;
};

export const customGetStateFromPath = (
    path: string,
    opts: Parameters<typeof getStateFromPath<RootStackParamList>>[1],
    context: LinkingContext
): NavigationState => {
    if (isBlacklistedRoute(path) || isUntrustedWebViewPath(path, opts)) {
        return { routes: [{ name: 'Splash' }] };
    }

    // Handle web-specific processing
    if (isWeb) {
        handleWebSpecificProcessing(path);
    }

    // Handle authentication protection
    const authResult = handleAuthProtection(path, context);
    if (authResult !== null) {
        return authResult;
    }

    const picksActive = context.isProductActive(Product.Pickem);
    const sweepsActive = context.isProductActive(Product.Sweeps);
    const arcadeActive = context.isProductActive(Product.Arcade);
    const casinoActive = context.isProductActive(Product.Casino);
    const predictionActive = context.isProductActive(Product.Prediction);
    const ripsActive = context.isProductActive(Product.Rips);
    const picksAvailable = context.isProductAvailable(Product.Pickem);
    const sweepsAvailable = context.isProductAvailable(Product.Sweeps);
    const arcadeAvailable = context.isProductAvailable(Product.Arcade);
    const casinoAvailable = context.isProductAvailable(Product.Casino);
    const predictionAvailable = context.isProductAvailable(Product.Prediction);
    const ripsAvailable = context.isProductAvailable(Product.Rips);
    const picksHiddenByUser = context.isProductHiddenByUser(Product.Pickem);
    const sweepsHiddenByUser = context.isProductHiddenByUser(Product.Sweeps);
    const arcadeHiddenByUser = context.isProductHiddenByUser(Product.Arcade);
    const casinoHiddenByUser = context.isProductHiddenByUser(Product.Casino);
    const predictionHiddenByUser = context.isProductHiddenByUser(Product.Prediction);
    const ripsHiddenByUser = context.isProductHiddenByUser(Product.Rips);

    // Handle /promos deep link
    const promoHubResult = handlePromoHubRoute(path);
    if (promoHubResult !== null) {
        return promoHubResult;
    }

    // Handle <prefix>/<product> paths — trigger product transition overlay for product switching
    const productTransitionResult = handleProductTransitionRoute(path);
    if (productTransitionResult !== null) {
        return productTransitionResult;
    }

    const productHandlers = [
        () => handlePicksRoute(path, picksActive, picksAvailable, picksHiddenByUser, opts),
        () => handleArcadeRoute(path, arcadeActive, arcadeAvailable, arcadeHiddenByUser, opts),
        () => handleCasinoRoute(path, casinoActive, casinoAvailable, casinoHiddenByUser, opts),
        () => handlePredictionRoute(path, predictionActive, predictionAvailable, predictionHiddenByUser, opts),
        () => handleSweepsRoute(path, sweepsActive, sweepsAvailable, sweepsHiddenByUser, opts),
        () => handleRipsRoute(path, ripsActive, ripsAvailable, ripsHiddenByUser, opts),
    ];

    for (const handler of productHandlers) {
        const result = handler();
        if (result) {
            return enhanceNavigationState(result);
        }
    }

    const defaultState = handleDefaultRoute(
        redirectDepositToCoinShop(path),
        context.availableProducts,
        picksActive || sweepsActive || arcadeActive || casinoActive || predictionActive || ripsActive,
        opts
    );

    return enhanceNavigationState(defaultState);
};

/**
 * Post-processes navigation state from deeplinks.
 * Ensures FantasyEventsSheets gets a fresh contextKey (forces remount)
 * and resets the events tab store to avoid stale index from a previous league.
 */
const enhanceNavigationState = (state: NavigationState): NavigationState => {
    if (!state?.routes) {
        return state;
    }

    const fantasyEventsRoute = state.routes.find(
        r => r.name === 'FantasyEventsSheets' && r.params && 'playerId' in r.params
    );

    if (!fantasyEventsRoute) {
        return state;
    }

    const params = fantasyEventsRoute.params as Record<string, unknown>;

    useEventsTabsStore.getState().actions.setSelectedTab(0);

    const fantasyEventsRouteIndex = state.routes.indexOf(fantasyEventsRoute);

    return {
        ...state,
        routes: state.routes.map((r, i) =>
            i === fantasyEventsRouteIndex
                ? {
                      ...r,
                      params: {
                          ...params,
                          contextKey: `deeplink-${params.eventId}-${params.playerId}`,
                      },
                  }
                : r
        ),
    };
};
